import { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { Multiselect } from 'multiselect-react-dropdown';
import { countriesList } from './utils/countries';
import useAuth from '../auth/useAuth';
import AuthLayout from './components/AuthLayout';
import AuthTitle from './components/AuthTitle';
import './RegisterPage.css';

const SignupSchema = Yup.object({
  firstName: Yup.string().min(3, 'First Name must be at least 3 characters').required('First Name is required'),
  lastName: Yup.string().min(2, 'Last Name must be at least 2 characters'),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match').required('Confirm Password is required'),
  companyName: Yup.string().min(3, 'Company name must be at least 3 characters').required('Company name is required'),
  phoneNumber: Yup.string().min(10, 'Phone number must be at least 10 characters').required('Phone number is required'),
  teams: Yup.string().min(3, 'Teams must be at least 3 characters').required('Teams is required'),
  telegram: Yup.string().min(5, 'Telegram must be at least 5 characters'),
  website: Yup.string(),
  trafficSourcesMethodList: Yup.array().min(1, 'Traffic sources method must be at least 1 selection').required('Traffic sources method is required'),
  trafficSourcesGeoList: Yup.array().min(1, 'Traffic sources geo must be at least 1 selection').required('Traffic sources geo is required'),
  estimatedMonthlyFtdsRange: Yup.string().min(1, 'Estimated monthly FTD should be selected').required('Estimated monthly FTD is required'),
  proposedDealType: Yup.string().min(1, 'Proposed Deal Type should be selected').required('Proposed Deal Type is required'),
  recaptcha: Yup.string().required('ReCaptcha is required'),
  terms: Yup.boolean().isTrue('Required'),
});

const RegisterPage = () => {
  const auth = useAuth();
  const history = useHistory();
  const [outcome, setOutcome] = useState(null);
  const [failed, setFailed] = useState(false);
  const [erCaptcha, setErCaptcha] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const reCaptchaRef = useRef();

  let referralCode;
  const cookies = document.cookie;
  const refCode = cookies.split(';')[0].split('=');
  if (refCode[0] === 'referral_code') {
    referralCode = refCode[1];
  }

  const countryList = useMemo(() => countriesList, []);

  const submitForm = (values) => {
    const { recaptcha, ...rest } = values;
    setOutcome(null);
    setFailed(false);
    setErCaptcha(false);
    
    if (captcha) {
      auth.register({
        affiliate: { ...rest, referralCode },
        gRecaptchaResponse: captcha,
      }).then((response) => {
        setOutcome(response['outcome']);
      }).catch((err) => {
        setOutcome(err.response?.data?.outcome);
        setFailed(true);
        reCaptchaRef.current.reset();
      });
    } else {
      setErCaptcha(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '', password: '', firstName: '', lastName: '', confirmPassword: '',
      companyName: '', phoneNumber: '', teams: '', telegram: '', website: '',
      trafficSourcesMethodList: [], trafficSourcesGeoList: [],
      estimatedMonthlyFtdsRange: 'ZERO_TO_FIFTY', proposedDealType: 'REV_SHARE',
      message: '', recaptcha: '', terms: false,
    },
    validationSchema: SignupSchema,
    onSubmit: submitForm,
  });

  const handleCaptchaChange = useCallback((value) => {
    setCaptcha(value);
    formik.setFieldValue('recaptcha', value);
  }, []);

  const handleCheckboxArrayChange = (fieldName) => (event) => {
    const { value, checked } = event.target;
    const currentArray = formik.values[fieldName];
    const newArray = checked 
      ? (currentArray.includes(value) ? currentArray : [...currentArray, value])
      : currentArray.filter(item => item !== value);
    formik.setFieldValue(fieldName, newArray);
  };

  const onGEOSelect = useCallback((selectedList, selectedItem) => {
    const currentArray = formik.values['trafficSourcesGeoList'];
    const newArray = currentArray.includes(selectedItem.code) ? currentArray : [...currentArray, selectedItem.code];
    formik.setFieldValue('trafficSourcesGeoList', newArray);
  }, [formik]);

  const onGEORemove = useCallback((selectedList, selectedItem) => {
    const currentArray = formik.values['trafficSourcesGeoList'];
    const newArray = currentArray.filter(item => item !== selectedItem.code);
    formik.setFieldValue('trafficSourcesGeoList', newArray);
  }, [formik]);

  return (
    <AuthLayout>
      <div className="register-page__container">
        <AuthTitle>Sign Up</AuthTitle>
        <p className="register-page__subtitle">
          Already have an account?{' '}
          <a href="/login" onClick={(e) => { e.preventDefault(); history.push('/login'); }}>
            Sign In
          </a>
        </p>

        {failed && (
          <div className="register-page__alert register-page__alert--error">
            {outcome === 'EMAIL_ALREADY_USED' && 'This email address is already used'}
            {outcome === 'FORM_ERROR' && 'Please check if the form is filled out correctly'}
            {(outcome !== 'FORM_ERROR' && outcome !== 'EMAIL_ALREADY_USED') && 'There was an error, please try again later'}
          </div>
        )}

        {outcome === 'SUCCESS' ? (
          <div className="register-page__alert register-page__alert--success">
            Registration Successful - Please check your email for further information
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit} className="register-page__form">
            <div className="register-page__row">
              <input type="text" name="firstName" placeholder="First Name" value={formik.values.firstName} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.firstName && formik.errors.firstName ? 'register-page__input--error' : ''}`} />
              <input type="text" name="lastName" placeholder="Last Name" value={formik.values.lastName} onChange={formik.handleChange} onBlur={formik.handleBlur} className="register-page__input" />
            </div>
            {formik.touched.firstName && formik.errors.firstName && <span className="register-page__error">{formik.errors.firstName}</span>}

            <input type="email" name="email" placeholder="Email" value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.email && formik.errors.email ? 'register-page__input--error' : ''}`} />
            {formik.touched.email && formik.errors.email && <span className="register-page__error">{formik.errors.email}</span>}

            <div className="register-page__row">
              <input type="password" name="password" placeholder="Password" value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.password && formik.errors.password ? 'register-page__input--error' : ''}`} />
              <input type="password" name="confirmPassword" placeholder="Confirm Password" value={formik.values.confirmPassword} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'register-page__input--error' : ''}`} />
            </div>
            {formik.touched.password && formik.errors.password && <span className="register-page__error">{formik.errors.password}</span>}
            {formik.touched.confirmPassword && formik.errors.confirmPassword && <span className="register-page__error">{formik.errors.confirmPassword}</span>}

            <input type="text" name="companyName" placeholder="Company Name" value={formik.values.companyName} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.companyName && formik.errors.companyName ? 'register-page__input--error' : ''}`} />
            {formik.touched.companyName && formik.errors.companyName && <span className="register-page__error">{formik.errors.companyName}</span>}

            <input type="tel" name="phoneNumber" placeholder="Phone number" value={formik.values.phoneNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.phoneNumber && formik.errors.phoneNumber ? 'register-page__input--error' : ''}`} />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && <span className="register-page__error">{formik.errors.phoneNumber}</span>}

            <input type="text" name="teams" placeholder="Teams" value={formik.values.teams} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`register-page__input ${formik.touched.teams && formik.errors.teams ? 'register-page__input--error' : ''}`} />
            {formik.touched.teams && formik.errors.teams && <span className="register-page__error">{formik.errors.teams}</span>}

            <input type="text" name="telegram" placeholder="Telegram" value={formik.values.telegram} onChange={formik.handleChange} onBlur={formik.handleBlur} className="register-page__input" />
            <input type="text" name="website" placeholder="Website" value={formik.values.website} onChange={formik.handleChange} onBlur={formik.handleBlur} className="register-page__input" />

            <div className="register-page__field-group">
              <label className="register-page__label">Traffic Sources (Method) *</label>
              <div className="register-page__checkboxes">
                {['SEO', 'PPC', 'Email', 'Other'].map(method => (
                  <label key={method} className="register-page__checkbox-label">
                    <input type="checkbox" value={method} checked={formik.values.trafficSourcesMethodList.includes(method)} onChange={handleCheckboxArrayChange('trafficSourcesMethodList')} />
                    {method}
                  </label>
                ))}
              </div>
              {formik.touched.trafficSourcesMethodList && formik.errors.trafficSourcesMethodList && <span className="register-page__error">{formik.errors.trafficSourcesMethodList}</span>}
            </div>

            <div className="register-page__field-group">
              <label className="register-page__label">Traffic Sources (GEOs) *</label>
              <Multiselect 
                options={countryList} 
                displayValue="name" 
                onSelect={onGEOSelect} 
                onRemove={onGEORemove} 
                avoidHighlightFirstOption 
                style={{
                  optionContainer: {
                    background: '#FFFFFF',
                    color: '#1C1C1C'
                  },
                  option: {
                    color: '#1C1C1C'
                  }
                }}
              />
              {formik.touched.trafficSourcesGeoList && formik.errors.trafficSourcesGeoList && <span className="register-page__error">{formik.errors.trafficSourcesGeoList}</span>}
            </div>

            <div className="register-page__field-group">
              <label className="register-page__label">Estimated monthly FTDs *</label>
              <div className="register-page__radios">
                {[{v:'ZERO_TO_FIFTY',l:'0-50'},{v:'FIFTY_ONE_TO_ONE_HUNDRED',l:'51-100'},{v:'ONE_HUNDRED_AND_ONE_TO_TWO_HUNDRED',l:'101-200'},{v:'TWO_HUNDRED_AND_ONE_TO_THREE_HUNDRED',l:'201-300'},{v:'THREE_HUNDRED_PLUS',l:'300+'}].map(({v,l}) => (
                  <label key={v} className="register-page__radio-label">
                    <input type="radio" name="estimatedMonthlyFtdsRange" value={v} checked={formik.values.estimatedMonthlyFtdsRange === v} onChange={formik.handleChange} />
                    {l}
                  </label>
                ))}
              </div>
            </div>

            <div className="register-page__field-group">
              <label className="register-page__label">Proposed Deal Type *</label>
              <div className="register-page__radios">
                {[{v:'REV_SHARE',l:'Revshare'},{v:'CPA',l:'CPA'},{v:'HYBRID',l:'Hybrid'}].map(({v,l}) => (
                  <label key={v} className="register-page__radio-label">
                    <input type="radio" name="proposedDealType" value={v} checked={formik.values.proposedDealType === v} onChange={formik.handleChange} />
                    {l}
                  </label>
                ))}
              </div>
            </div>

            <textarea name="message" placeholder="Message" rows="4" value={formik.values.message} onChange={formik.handleChange} className="register-page__textarea" />

            <label className="register-page__terms-label">
              <input type="checkbox" name="terms" checked={formik.values.terms} onChange={formik.handleChange} />
              I accept the <a href="/privacy" target="_blank" rel="noreferrer">terms and conditions</a>
            </label>
            {formik.errors.terms && <span className="register-page__error">{formik.errors.terms}</span>}

            <div className="register-page__action-row">
              <div className="register-page__captcha">
                <ReCAPTCHA ref={reCaptchaRef} sitekey="6Lc6O9AUAAAAAF6myYURrvZwW43E4ShPeKOVKCHe" onChange={handleCaptchaChange} />
                {((formik.touched.recaptcha && formik.errors.recaptcha) || erCaptcha) && <span className="register-page__error">{formik.errors.recaptcha || 'ReCaptcha error'}</span>}
              </div>
              <button type="submit" className="register-page__button">Sign Up</button>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
